// Registro de generaciones en curso — ADR-2026-09-06-registro-de-generaciones-en-curso.
//
// Las tandas del panel (`TandaPanel`) vivían en un `useState` de `InterfazV2`: cerrar el
// sheet, volver a la home o ir a Creaciones las borraba mientras la promesa seguía viva
// y el backend cobraba igual ("generé y desapareció", el usuario vuelve a generar y paga
// dos veces). Y la reanudación tras recarga usaba UNA clave de `localStorage`
// (`atlas-v2-pending-generation`) que la segunda tanda pisaba y cualquier cierre borraba.
//
// Acá viven a nivel app, en un store chiquito sin React (lo consumen los hooks de
// `useGeneracionesEnCurso.ts` con `useSyncExternalStore`, y también las promesas de
// generación aunque el componente que las disparó ya no exista) y persistido en
// `localStorage` como LISTA de ids —nunca URLs firmadas: caducan y rompen al
// rehidratar—. Todo lo de este módulo es puro y se testea en `generacionesEnCurso.test.ts`.
//
// Se filtra SIEMPRE por `companyId` (lección de
// ADR-2026-08-20-el-cache-de-sesion-cruzaba-cuentas): el storage es del navegador, no de
// la cuenta, y una tanda de otra empresa no tiene que verse ni contar.

import type { ImagenDeTanda, TandaPanel } from './components/PanelTandaCreaciones';

export interface GeneracionEnCurso extends TandaPanel {
  /** Empresa dueña de la tanda. Las listas se filtran por ésta, siempre. */
  companyId: string;
  /**
   * Los `execution_id` de los requests de la tanda: por ellos se reanuda el poll tras
   * una recarga. Vacío = no verificable (carrusel/animar delegados desde el visor,
   * ADR-2026-09-06-carrusel-y-animar-aterrizan-en-la-columna): vive sólo en memoria y
   * se descarta al rehidratar.
   */
  executionIds: string[];
  /** La tanda produce video: edad máxima más larga y filtro "videos" en Creaciones. */
  esVideo: boolean;
  /**
   * Créditos comprometidos por esta tanda (lo que mostraba el pill al generar). El guard
   * de créditos los descuenta del saldo mientras la tanda no termine
   * (ADR-2026-09-06-tope-de-tres-tandas-en-curso). Sin dato se toma `total`.
   */
  creditos?: number;
  /**
   * Cuánto se estima que tarda (ms), para pacear la barra de sus recuadros. Lo calcula
   * quien genera con la configuración del pedido (`utils/estimacionVideo`); sin dato, el
   * recuadro usa el default de imagen o de video
   * (ADR-2026-09-18-la-barra-del-video-se-pacea-con-lo-que-tarda-de-verdad).
   */
  duracionEstimadaMs?: number;
}

/**
 * Tope de tandas NO terminadas por empresa (ADR-2026-09-06-tope-de-tres-tandas-en-curso,
 * ADR-2026-09-14-el-tope-de-tandas-en-curso-sube-a-seis).
 *
 * Empezó en 3 por el cap de ~100 jobs activos del backend (3 × 18 requests máximos
 * entraban). Va en 6 porque una producción de plantillas son DOS tandas (la imagen base
 * y el carrusel que la sigue) y con 3 el usuario se quedaba sin lugar a la segunda
 * producción. El copy de los avisos sale de esta constante: nunca se escribe el número.
 */
export const MAX_TANDAS_EN_CURSO = 6;
/** Más vieja que esto, una tanda rehidratada se descarta: si salió, ya está en el historial. */
export const EDAD_MAXIMA_MS = 15 * 60_000;
/** Los videos tardan más (Kling: 3-10 min), así que se les da más margen. */
export const EDAD_MAXIMA_VIDEO_MS = 20 * 60_000;
export const CLAVE_STORAGE = 'atlas-v2-generaciones-en-curso';

/** Lo mínimo de `Storage` que usa el store: así el test inyecta un Map. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

/** Lo que se escribe en el storage. Sin `src`: las URLs firmadas caducan. */
interface GeneracionPersistida {
  id: string;
  companyId: string;
  executionIds: string[];
  total: number;
  ratio: number;
  modo?: string;
  producto?: string;
  creadaEn: number;
  esVideo: boolean;
  terminada: boolean;
  imagenIds: string[];
  creditos?: number;
  duracionEstimadaMs?: number;
}

export type Updater = GeneracionEnCurso[] | ((prev: GeneracionEnCurso[]) => GeneracionEnCurso[]);

export interface Store {
  /** Snapshot ESTABLE: la misma referencia hasta el próximo `set`. `useSyncExternalStore`
   *  lo exige (con una referencia nueva por lectura entra en loop infinito). */
  get(): GeneracionEnCurso[];
  subscribe(cb: () => void): () => void;
  set(lista: Updater): void;
  /** Suma una tanda ARRIBA (la más nueva primero) y devuelve su id (uuid). */
  agregar(g: Omit<GeneracionEnCurso, 'id'>): string;
  actualizar(id: string, parcial: Partial<GeneracionEnCurso>): void;
  /** La tanda terminó: `terminada: true`, `total` = lo que llegó (la previsión era eso,
   *  una previsión) e `imagenes`. */
  cerrar(id: string, imagenes: ImagenDeTanda[]): void;
  quitar(id: string): void;
  /** Saca las terminadas (de una empresa, o de todas). Las que siguen corriendo se
   *  quedan: es lo que hace que cerrar el sheet no las pierda. */
  descartarTerminadas(companyId?: string | null): void;
  /** Saca las NO terminadas que pasaron su edad máxima (promesa muerta en esta pestaña). */
  purgarViejas(): void;
  /** Cuántos `set` hubo. */
  version(): number;
  /** Cuántas veces se pidió recarga a las grillas (ver `pedirRecarga`). */
  recargaVersion(): number;
  /**
   * Empuja a las grillas (columna del modo, Creaciones) a volver a pedir la lista:
   * pasa cuando una tanda cierra sin imágenes que dibujar o cuando el reanudador
   * terminó un poll — las filas ya están en la base y entran por la grilla
   * (ADR-2026-09-04-la-generacion-del-modo-nunca-cambia-de-pantalla).
   */
  pedirRecarga(): void;
  /**
   * Ids de las tandas cuya PROMESA vive en esta pestaña (se crearon acá). Las
   * rehidratadas no están: son las que el reanudador tiene que pollear.
   */
  idsVivos: Set<string>;
}

// ─── Helpers puros ───────────────────────────────────────────────────────

const edadMaxima = (g: Pick<GeneracionEnCurso, 'esVideo'>): number =>
  g.esVideo ? EDAD_MAXIMA_VIDEO_MS : EDAD_MAXIMA_MS;

export function serializar(lista: GeneracionEnCurso[]): string {
  const persistidas: GeneracionPersistida[] = lista.map((g) => ({
    id: g.id,
    companyId: g.companyId,
    executionIds: g.executionIds ?? [],
    total: g.total,
    ratio: g.ratio,
    modo: g.modo,
    producto: g.producto,
    creadaEn: g.creadaEn,
    esVideo: !!g.esVideo,
    terminada: !!g.terminada,
    imagenIds: (g.imagenes ?? []).map((i) => String(i.id)),
    ...(typeof g.creditos === 'number' ? { creditos: g.creditos } : {}),
    ...(typeof g.duracionEstimadaMs === 'number'
      ? { duracionEstimadaMs: g.duracionEstimadaMs }
      : {}),
  }));
  return JSON.stringify(persistidas);
}

/**
 * Lee lo persistido tolerando basura: JSON roto, `null`, entradas sin `id`/`companyId`.
 * Descarta las vencidas (no hay nada que reanudar: si salió, está en el historial), las
 * terminadas (su `src` no viajó) y las sin `executionIds` (no verificables).
 */
export function rehidratar(raw: string | null, ahora: number): GeneracionEnCurso[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const out: GeneracionEnCurso[] = [];
  for (const p of parsed as Partial<GeneracionPersistida>[]) {
    if (!p || typeof p !== 'object') continue;
    if (typeof p.id !== 'string' || !p.id) continue;
    if (typeof p.companyId !== 'string' || !p.companyId) continue;
    if (p.terminada) continue;
    const executionIds = Array.isArray(p.executionIds)
      ? p.executionIds.filter((x): x is string => typeof x === 'string' && !!x)
      : [];
    if (executionIds.length === 0) continue;
    const creadaEn = typeof p.creadaEn === 'number' ? p.creadaEn : 0;
    const esVideo = !!p.esVideo;
    if (ahora - creadaEn > edadMaxima({ esVideo })) continue;
    out.push({
      id: p.id,
      companyId: p.companyId,
      executionIds,
      total: typeof p.total === 'number' ? p.total : 1,
      ratio: typeof p.ratio === 'number' && p.ratio > 0 ? p.ratio : 0.8,
      modo: typeof p.modo === 'string' ? p.modo : undefined,
      producto: typeof p.producto === 'string' ? p.producto : undefined,
      creadaEn,
      esVideo,
      terminada: false,
      // Los ids vuelven; el `src` no viajó (caduca), así que quedan sin dibujar. En la
      // práctica una tanda no terminada no tiene imágenes: `cerrar` pone las dos cosas.
      imagenes: (Array.isArray(p.imagenIds) ? p.imagenIds : []).map((id) => ({
        id: String(id),
        src: '',
      })),
      ...(typeof p.creditos === 'number' ? { creditos: p.creditos } : {}),
      ...(typeof p.duracionEstimadaMs === 'number' && p.duracionEstimadaMs > 0
        ? { duracionEstimadaMs: p.duracionEstimadaMs }
        : {}),
    });
  }
  return out;
}

export function filtrarPorEmpresa(
  lista: GeneracionEnCurso[],
  companyId: string | null | undefined,
): GeneracionEnCurso[] {
  if (!companyId) return [];
  return lista.filter((g) => g.companyId === companyId);
}

/** Las que el reanudador tiene que pollear: no terminadas, verificables y sin promesa
 *  viva en esta pestaña. */
export function paraReanudar(
  lista: GeneracionEnCurso[],
  idsVivos: Set<string>,
  _ahora: number,
): GeneracionEnCurso[] {
  void _ahora;
  return lista.filter(
    (g) => !g.terminada && g.executionIds.length > 0 && !idsVivos.has(g.id),
  );
}

/** Cuánto le queda de vida a una tanda rehidratada = tope del poll de reanudación.
 *  Nunca menos de 60 s: un poll de cero rondas no comprueba nada. */
export function edadRestanteMs(g: GeneracionEnCurso, ahora: number): number {
  return Math.max(60_000, edadMaxima(g) - (ahora - g.creadaEn));
}

export interface CeldaGenerando {
  /** Única por recuadro (`<tandaId>-<índice>`), para la key de React. */
  id: string;
  tandaId: string;
  ratio: number;
  creadaEn: number;
  esVideo: boolean;
  producto?: string;
  /** La estimación de su tanda, para pacear la barra. */
  duracionEstimadaMs?: number;
}

export type FiltroContenido = 'all' | 'images' | 'videos';

/**
 * Los recuadros "Generando…" que le faltan a cada tanda no terminada
 * (`total − imagenes.length`), de la tanda más nueva a la más vieja, filtrados como
 * Creaciones filtra su grilla (imágenes / videos / todo).
 */
export function celdasGenerando(
  lista: GeneracionEnCurso[],
  filtro: FiltroContenido,
): CeldaGenerando[] {
  const enCurso = lista
    .filter((g) => !g.terminada)
    .filter((g) =>
      filtro === 'videos' ? !!g.esVideo : filtro === 'images' ? !g.esVideo : true,
    )
    .sort((a, b) => b.creadaEn - a.creadaEn);
  const out: CeldaGenerando[] = [];
  for (const g of enCurso) {
    const faltan = Math.max(0, g.total - (g.imagenes?.length ?? 0));
    for (let i = 0; i < faltan; i++) {
      out.push({
        id: `${g.id}-${i}`,
        tandaId: g.id,
        ratio: g.ratio,
        creadaEn: g.creadaEn,
        esVideo: !!g.esVideo,
        producto: g.producto,
        duracionEstimadaMs: g.duracionEstimadaMs,
      });
    }
  }
  return out;
}

/** Créditos comprometidos por las tandas NO terminadas de la empresa: el guard los
 *  descuenta del saldo (ADR-2026-09-06-tope-de-tres-tandas-en-curso). */
export function creditosComprometidos(
  lista: GeneracionEnCurso[],
  companyId: string | null | undefined,
): number {
  return filtrarPorEmpresa(lista, companyId)
    .filter((g) => !g.terminada)
    .reduce((acc, g) => acc + (typeof g.creditos === 'number' ? g.creditos : g.total), 0);
}

/**
 * Reparte la reserva de créditos de una producción de plantillas entre sus DOS tandas
 * (ADR-2026-09-14-la-produccion-abre-todos-sus-recuadros-al-generar).
 *
 * El costo del stepper (`costoTotal` = costo de una imagen × cantidad) se compromete de
 * una sola vez al generar, pero la producción son dos tandas y la base **cierra** antes
 * de que salga el pedido del carrusel. Con todo el costo en la base y `0` en el
 * carrusel, la cuenta cuadra mientras la base corre y se va a cero al cerrarla, con las
 * N−1 del carrusel todavía generando: otra generación pasa el guard usando créditos ya
 * gastados y falla al final con "probá de nuevo" — justo lo que el guard evita
 * (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
 *
 * Por eso se reparte desde el nacimiento en vez de transferir al cerrar: la suma da
 * exactamente `costoTotal` mientras las dos corren, y cuando la base cierra queda en pie
 * lo que falta generar. A la base le toca su imagen (redondeada PARA ARRIBA: con costos
 * que no dividen justo, preferimos reservar de más) y al carrusel el resto.
 *
 * Sin un costo computable devuelve `undefined` en las dos: `creditosComprometidos` cae
 * entonces al `total` de cada tanda, que para el carrusel es 1 crédito por imagen — el
 * costo del endpoint de ángulos.
 */
export function repartirCreditosDeProduccion(
  costoTotal: number | null | undefined,
  carruselCount: number,
): { base: number | undefined; carrusel: number | undefined } {
  if (typeof costoTotal !== 'number' || !(costoTotal > 0) || carruselCount < 1) {
    return { base: costoTotal ?? undefined, carrusel: undefined };
  }
  const base = Math.ceil(costoTotal / (carruselCount + 1));
  return { base, carrusel: Math.max(0, costoTotal - base) };
}

export function puedeAgregar(
  lista: GeneracionEnCurso[],
  companyId: string | null | undefined,
): boolean {
  if (!companyId) return false;
  const enCurso = filtrarPorEmpresa(lista, companyId).filter((g) => !g.terminada).length;
  return enCurso < MAX_TANDAS_EN_CURSO;
}

/**
 * Tope del poll de una tanda del panel, proporcional a su tamaño: `clamp(total × 60 s,
 * 5 min, 15 min)`. El worker procesa de a 5 en total, así que dos tandas grandes ya
 * superaban los 5 min fijos y cerraban "terminadas" con menos imágenes
 * (ADR-2026-09-06-tope-de-tres-tandas-en-curso).
 */
export function timeoutPollTanda(total: number): number {
  const min = 5 * 60_000;
  const max = 15 * 60_000;
  return Math.min(max, Math.max(min, Math.max(1, total) * 60_000));
}

// ─── Store ───────────────────────────────────────────────────────────────

const uuid = (): string => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // Fallback sin `crypto` (entornos viejos): suficiente para no chocar en una pestaña.
  return `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const storageEnMemoria = (): StorageLike => {
  const mapa = new Map<string, string>();
  return {
    getItem: (k) => mapa.get(k) ?? null,
    setItem: (k, v) => {
      mapa.set(k, v);
    },
    removeItem: (k) => {
      mapa.delete(k);
    },
  };
};

export function crearStore(deps?: { storage?: StorageLike; ahora?: () => number }): Store {
  const ahora = deps?.ahora ?? (() => Date.now());
  const storage = deps?.storage ?? storageEnMemoria();
  const leer = (): string | null => {
    try {
      return storage.getItem(CLAVE_STORAGE);
    } catch {
      return null;
    }
  };
  const escribir = (lista: GeneracionEnCurso[]) => {
    try {
      storage.setItem(CLAVE_STORAGE, serializar(lista));
    } catch {
      /* modo privado / cuota llena: el registro sigue en memoria */
    }
  };

  let lista: GeneracionEnCurso[] = rehidratar(leer(), ahora());
  let ver = 0;
  let recargas = 0;
  const subs = new Set<() => void>();
  const idsVivos = new Set<string>();
  const notificar = () => {
    for (const cb of Array.from(subs)) cb();
  };

  const set = (u: Updater) => {
    lista = typeof u === 'function' ? u(lista) : u;
    ver += 1;
    escribir(lista);
    notificar();
  };

  return {
    get: () => lista,
    subscribe: (cb) => {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
    set,
    agregar: (g) => {
      const id = uuid();
      idsVivos.add(id);
      set((prev) => [{ ...g, id }, ...prev]);
      return id;
    },
    actualizar: (id, parcial) => {
      set((prev) => prev.map((g) => (g.id === id ? { ...g, ...parcial } : g)));
    },
    cerrar: (id, imagenes) => {
      idsVivos.delete(id);
      set((prev) =>
        prev.map((g) =>
          g.id === id
            ? { ...g, imagenes, total: imagenes.length, terminada: true }
            : g,
        ),
      );
    },
    quitar: (id) => {
      idsVivos.delete(id);
      set((prev) => prev.filter((g) => g.id !== id));
    },
    descartarTerminadas: (companyId) => {
      set((prev) =>
        prev.filter(
          (g) => !g.terminada || (companyId != null && g.companyId !== companyId),
        ),
      );
    },
    purgarViejas: () => {
      const t = ahora();
      const quedan = lista.filter(
        (g) => g.terminada || t - g.creadaEn <= edadMaxima(g),
      );
      if (quedan.length === lista.length) return;
      for (const g of lista) if (!quedan.includes(g)) idsVivos.delete(g.id);
      set(quedan);
    },
    version: () => ver,
    recargaVersion: () => recargas,
    pedirRecarga: () => {
      recargas += 1;
      notificar();
    },
    idsVivos,
  };
}

/**
 * `localStorage` envuelto: en modo privado, con la cuota llena o fuera del navegador
 * (tests, SSR) el acceso mismo puede tirar, y el registro tiene que seguir andando en
 * memoria.
 */
const storageSeguro = (): StorageLike => {
  // Fuera del navegador (vitest) no hay dónde persistir; Node además avisa por consola
  // al tocar su `localStorage` experimental.
  if (typeof window === 'undefined') return storageEnMemoria();
  try {
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (ls && typeof ls.getItem === 'function') return ls;
  } catch {
    /* acceso bloqueado */
  }
  return storageEnMemoria();
};

/** El registro de la app. */
export const generacionesStore: Store = crearStore({ storage: storageSeguro() });
/** Atajo al set de tandas vivas del registro de la app (para `paraReanudar`). */
export const idsVivos = generacionesStore.idsVivos;

// ─── El aviso de "generando de fondo" ────────────────────────────────────
// ADR-2026-09-06-el-aviso-de-generando-de-fondo.

/**
 * Cuántas pantallas están MOSTRANDO la carga ahora mismo: la columna de resultados de
 * un modo, la pantalla de carga full-screen. Mientras haya una, el aviso flotante se
 * calla — sería el mismo dato dos veces en la misma pantalla.
 *
 * Es un contador con inscripciones y no un booleano por ruta: qué pantalla dibuja la
 * carga lo sabe cada componente al montarse, no una lista de paths que hay que mantener
 * (y que se equivocaría con los modos viejos, que viven en la misma ruta que los nuevos
 * pero no tienen columna). Cada `registrar()` devuelve SU baja, y bajar dos veces la
 * misma inscripción no resta de más: es lo que pasa con un cleanup de efecto que corre
 * dos veces en StrictMode.
 */
export interface ContadorDePantallas {
  registrar(): () => void;
  cantidad(): number;
  subscribe(cb: () => void): () => void;
}

export function crearContadorDePantallas(): ContadorDePantallas {
  let inscriptas = 0;
  const subs = new Set<() => void>();
  const avisar = () => subs.forEach((cb) => cb());
  return {
    registrar() {
      inscriptas += 1;
      avisar();
      let liberada = false;
      return () => {
        if (liberada) return;
        liberada = true;
        inscriptas -= 1;
        avisar();
      };
    },
    cantidad: () => inscriptas,
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },
  };
}

/** Las pantallas de la app que están mostrando la carga. */
export const pantallasDeCarga: ContadorDePantallas = crearContadorDePantallas();

export interface AvisoGenerando {
  /** Imágenes que todavía no llegaron, sumadas sobre las tandas en curso que NO son de video. */
  imagenes: number;
  /** Videos que todavía no llegaron. Aparte: "Generando 1 imagen" por un video mentía
   *  (ADR-2026-09-18-la-barra-del-video-se-pacea-con-lo-que-tarda-de-verdad). */
  videos: number;
  /** El `kind` del modo, si TODO lo que corre es de uno solo; si no, `null`. */
  modo: string | null;
}

/**
 * Qué avisar, o `null` si no hay nada que avisar.
 *
 * Nada que avisar es: no queda ninguna imagen por llegar, o alguna pantalla ya está
 * mostrando la carga (`pantallasMostrando > 0`). El modo se nombra sólo cuando es
 * inequívoco: con tandas de dos modos distintos, decir uno sería mentir sobre el otro.
 * Las tandas viejas sin `modo` no votan — no bloquean el nombre de las que sí lo tienen.
 */
export function avisoGenerandoDeFondo(
  lista: GeneracionEnCurso[],
  pantallasMostrando: number,
): AvisoGenerando | null {
  if (pantallasMostrando > 0) return null;
  const enCurso = lista.filter((g) => !g.terminada);
  const pendientes = (g: GeneracionEnCurso) =>
    Math.max(0, g.total - (g.imagenes?.length ?? 0));
  const imagenes = enCurso.filter((g) => !g.esVideo).reduce((acc, g) => acc + pendientes(g), 0);
  const videos = enCurso.filter((g) => g.esVideo).reduce((acc, g) => acc + pendientes(g), 0);
  if (imagenes + videos === 0) return null;
  const modos = new Set(
    enCurso.map((g) => g.modo).filter((m): m is string => !!m),
  );
  return { imagenes, videos, modo: modos.size === 1 ? [...modos][0] : null };
}

/** El copy del aviso: "Generando 3 imágenes · Fotos de producto", "Generando 1 video ·
 *  Creador de video", "Generando 2 imágenes y 1 video…". */
export function textoAvisoGenerando(
  imagenes: number,
  videos: number,
  nombreModo: string | null,
): string {
  const partes: string[] = [];
  if (imagenes > 0) partes.push(`${imagenes} ${imagenes === 1 ? 'imagen' : 'imágenes'}`);
  if (videos > 0) partes.push(`${videos} ${videos === 1 ? 'video' : 'videos'}`);
  const cuenta = `Generando ${partes.join(' y ')}`;
  return nombreModo ? `${cuenta} · ${nombreModo}` : `${cuenta}…`;
}
